var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
const xilicaChannelTypes = ['gain', 'mute', 'string'];
const audioGainTypes = ['gain'];
const audioMuteTypes = ['mute'];
const allowedTypes = {
    mics: audioMuteTypes,
    micsMuteStatus: audioMuteTypes,
    amplifier: audioGainTypes,
    assistiveListeningMute: audioMuteTypes,
    assistiveListeningMuteStatus: audioMuteTypes,
    assistiveListeningVolume: audioGainTypes,
    audioInputMute: audioMuteTypes,
    audioInputMuteStatus: audioMuteTypes,
    audioInputVolume: audioGainTypes,
    overflowRoomMute: audioMuteTypes,
    overflowRoomMuteStatus: audioMuteTypes,
    overflowRoomVolume: audioGainTypes,
    soundReinforcementMute: audioMuteTypes,
    soundReinforcementMuteStatus: audioMuteTypes,
    soundReinforcementVolume: audioGainTypes,
    telephony: [],
    receiveGain: [],
    preset: [],
};
function isPresetMuteConfig(cfg) {
    return typeof cfg !== 'string';
}
export class XilicaSolaro {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = [];
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.channels.map(channel => ({
                    channel: channel.name,
                    filters: Object.fromEntries(Object.entries(allowedTypes).map(entry => [
                        entry[0],
                        entry[1].includes(channel.type),
                    ])),
                    type: channel.type,
                }));
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const channel = this.getChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                // This device doesn't support volume mute.
                if (((_a = channel.configInfo) === null || _a === void 0 ? void 0 : _a.type) === 'volume' &&
                    ((_b = channel.configInfo) === null || _b === void 0 ? void 0 : _b.filter) === 'amplifier') {
                    return false;
                }
                const match = yield this.socket.send(`GET ${key}`, new RegExp(`${key}=(TRUE|FALSE)|Error=\\d+`, 'i'), tags);
                return match[1] ? match[1] === 'TRUE' : this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d;
                const channel = this.getChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                // This device doesn't support volume mute.
                if (((_c = channel.configInfo) === null || _c === void 0 ? void 0 : _c.type) === 'volume' &&
                    ((_d = channel.configInfo) === null || _d === void 0 ? void 0 : _d.filter) === 'amplifier') {
                    return false;
                }
                let match;
                if (channel.mutePresets) {
                    const preset = value ? channel.mutePresets.mute : channel.mutePresets.unmute;
                    const isString = Number.isNaN(parseInt(preset, 10));
                    const presetName = isString ? `"${preset}"` : preset; // send strings with quotes
                    match = yield this.socket.send(`PRESET ${presetName}`, /(OK)|Error=\d+/i, tags);
                }
                else {
                    match = yield this.socket.send(`SET ${key} ${value ? 'TRUE' : 'FALSE'}`, /(OK)|Error=\d+/i, tags);
                }
                return match[1] ? value : this.state.mutes.values[key];
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    regExp: /^#(.+)=(TRUE|FALSE)$/i,
                    callback: (match) => {
                        const key = match[1];
                        if (key in this.state.mutes.values) {
                            this.state.mutes.update(key, match[2] === 'TRUE');
                        }
                    },
                },
            ],
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.getChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const match = yield this.socket.send(`GET ${key}`, new RegExp(`${key}=([-+]?[\\d\\.]+)|Error=\\d+`, 'i'), tags);
                return match[1] ? parseInt(match[1], 10) : this.state.volumes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.getChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const match = yield this.socket.send(`SET ${key} ${value}`, /(OK)|Error=\d+/i, tags);
                return match[1] ? value : this.state.volumes.values[key];
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 12, min: -12, round: 1 };
                return { max: 15, min: -35, round: 1 }; // volume
            }),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    regExp: /^#(.+)=([-+]?[\d\.]+)$/,
                    callback: (match) => {
                        const key = match[1];
                        if (key in this.state.volumes.values) {
                            this.state.volumes.update(key, parseInt(match[2], 10));
                        }
                    },
                },
            ],
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const tags = ['poll'];
                    yield this.socket.send('aveo.poll', /Error=101/i, tags);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.channels.forEach(channel => {
                delete channel.configInfo;
            });
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            // Subscribe for value updates on the channels that are being used.
            for (const channel of this.channels) {
                if (channel.configInfo === undefined)
                    continue;
                yield this.socket.send(`SUBSCRIBE ${channel.name}`, /OK|Error=\d+/i, ['control']);
            }
        });
    }
    onInitialize(credentials) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: 'INTERVAL 100',
                    succeed: /OK/i,
                    next: /ERROR=109/i,
                },
                {
                    errorCode: 'authFailed',
                    send: `LOGIN "${(_a = credentials.password) !== null && _a !== void 0 ? _a : 'admin'}"`,
                    succeed: /OK/i,
                    fail: /ERROR=\d+/i,
                },
            ], credentials);
            if (result === 'connected') {
                // Detect channels.
                const tags = ['initialize', 'get.detected'];
                this.channels.length = 0;
                const id = this.socket.registerAsync(/^#(.+)=(?:([-+]?\d+\.?\d*)|(TRUE|FALSE)|(.+))$/, (match) => {
                    const type = match[2] ? 'gain' : match[3] ? 'mute' : 'string';
                    const name = match[1];
                    const channel = this.getChannel(name);
                    if (channel === undefined) {
                        this.channels.push({ name, type });
                    }
                    else {
                        this.log.error(`multiple responses for channel: ${name}`);
                    }
                });
                try {
                    this.socket.sendAsync('REFRESH', tags);
                    yield this.socket.send('aveo.detect', /Error=101/i, tags);
                }
                finally {
                    this.socket.unregisterAsync(id);
                }
            }
            return result;
        });
    }
    // other class methods
    getChannel(config) {
        return this.channels.find(channel => channel.name === config);
    }
    getKey(config) {
        return isPresetMuteConfig(config) ? config.muteStatus : config;
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            // The device does not support detecting or validating presets, so we assume all presets are
            // valid and only validate channel names.
            const key = this.getKey(config);
            const channel = this.getChannel(key);
            if (channel === undefined)
                return 'missing';
            channel.configInfo = info;
            if (isPresetMuteConfig(config)) {
                channel.mutePresets = { mute: config.mutePreset, unmute: config.unmutePreset };
            }
            return 'ok';
        });
    }
}
// static properties
XilicaSolaro.channelsConfigName = 'solaroControlChannels';
XilicaSolaro.driverType = 'socketDriver';
XilicaSolaro.key = '-N-99Y3kNMVrsQk2RxV7';
XilicaSolaro.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\r'],
        },
        port: 10007,
        sendTerminator: '\r',
    },
];
